//src/pages/RelatorioPontos.js

import React, { useMemo, useState } from "react";
import axios from "../axiosConfig";
import ModalSemPermissao from "../components/ModalSemPermissao";
import * as XLSX from "xlsx";
import {
  AlertCircle,
  CalendarDays,
  Download,
  FileText,
  Search,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import "./RelatorioPontos.css";

function RelatorioPontos() {
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [relatorio, setRelatorio] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  const podeVerRelatorio =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some(
      (p) => p.rota === "relatorio-pontos" && p.pode_ler
    );

  const podeExportarRelatorio =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some(
      (p) => p.rota === "relatorio-pontos" && p.pode_exportar
    );

  const resumo = useMemo(() => {
    if (!relatorio) {
      return {
        ganhos: 0,
        usados: 0,
        expirados: 0,
        saldoLiquido: 0,
      };
    }

    const ganhos = Number(relatorio.ganhos || 0);
    const usados = Number(relatorio.usados || 0);
    const expirados = Number(relatorio.expirados || 0);

    return {
      ganhos,
      usados,
      expirados,
      saldoLiquido: ganhos - usados - expirados,
    };
  }, [relatorio]);

  if (!podeVerRelatorio) {
    return <ModalSemPermissao onClose={() => (window.location.href = "/")} />;
  }

  const gerarRelatorio = async () => {
    setErro("");

    if (!dataInicio || !dataFim) {
      setErro("Informe as datas de início e fim.");
      return;
    }

    setCarregando(true);

    try {
      const response = await axios.get("/relatorio-pontos", {
        params: { dataInicio, dataFim },
      });

      setRelatorio(response.data);
    } catch (err) {
      console.error("Erro ao buscar relatório:", err);
      setErro("Erro ao buscar relatório.");
    } finally {
      setCarregando(false);
    }
  };

  const exportarExcel = () => {
    if (!relatorio) return;

    const dados = [
      { Tipo: "Pontos Ganhos", Quantidade: resumo.ganhos },
      { Tipo: "Pontos Usados", Quantidade: resumo.usados },
      { Tipo: "Pontos Expirados", Quantidade: resumo.expirados },
      { Tipo: "Saldo Líquido", Quantidade: resumo.saldoLiquido },
    ];

    const planilha = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, planilha, "Relatório de Pontos");
    XLSX.writeFile(wb, "relatorio-pontos.xlsx");
  };

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  return (
    <div className="relatorioPontosAdmin-page">
      <div className="relatorioPontosAdmin-topbar">
        <div>
          <span className="relatorioPontosAdmin-kicker">Relatórios</span>
          <h2>Relatório de Pontos</h2>
          <p>
            Consulte pontos ganhos, usados e expirados em um período específico.
          </p>
        </div>
      </div>

      {erro && (
        <div className="relatorioPontosAdmin-alert danger">
          <AlertCircle size={16} />
          {erro}
        </div>
      )}

      <section className="relatorioPontosAdmin-filtrosCard">
        <div className="relatorioPontosAdmin-cardHeader">
          <div>
            <h3>Período do relatório</h3>
            <span>Selecione o intervalo para gerar os indicadores.</span>
          </div>

          <CalendarDays size={18} />
        </div>

        <div className="relatorioPontosAdmin-filtros">
          <div className="relatorioPontosAdmin-field">
            <label>Data início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </div>

          <div className="relatorioPontosAdmin-field">
            <label>Data fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>

          <button
            type="button"
            onClick={gerarRelatorio}
            className="relatorioPontosAdmin-btn primary"
            disabled={carregando}
          >
            <Search size={16} />
            {carregando ? "Gerando..." : "Gerar relatório"}
          </button>

          {podeExportarRelatorio && (
            <button
              type="button"
              onClick={exportarExcel}
              className="relatorioPontosAdmin-btn secondary"
              disabled={!relatorio}
            >
              <Download size={16} />
              Exportar Excel
            </button>
          )}
        </div>
      </section>

      <div className="relatorioPontosAdmin-cards">
        <div className="relatorioPontosAdmin-card success">
          <span>Pontos ganhos</span>
          <strong>{formatarNumero(resumo.ganhos)}</strong>
        </div>

        <div className="relatorioPontosAdmin-card warning">
          <span>Pontos usados</span>
          <strong>{formatarNumero(resumo.usados)}</strong>
        </div>

        <div className="relatorioPontosAdmin-card danger">
          <span>Pontos expirados</span>
          <strong>{formatarNumero(resumo.expirados)}</strong>
        </div>

        <div className="relatorioPontosAdmin-card">
          <span>Saldo líquido</span>
          <strong>{formatarNumero(resumo.saldoLiquido)}</strong>
        </div>
      </div>

      {relatorio && (
        <section className="relatorioPontosAdmin-tableCard">
          <div className="relatorioPontosAdmin-cardHeader">
            <div>
              <h3>Resumo consolidado</h3>
              <span>Resultado do período selecionado.</span>
            </div>

            <FileText size={18} />
          </div>

          <div className="relatorioPontosAdmin-tableWrapper">
            <table className="relatorioPontosAdmin-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Quantidade</th>
                  <th>Indicador</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>Pontos Ganhos</td>
                  <td>{formatarNumero(resumo.ganhos)}</td>
                  <td>
                    <span className="relatorioPontosAdmin-chip success">
                      <TrendingUp size={13} />
                      Entrada
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>Pontos Usados</td>
                  <td>{formatarNumero(resumo.usados)}</td>
                  <td>
                    <span className="relatorioPontosAdmin-chip warning">
                      <TrendingDown size={13} />
                      Uso
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>Pontos Expirados</td>
                  <td>{formatarNumero(resumo.expirados)}</td>
                  <td>
                    <span className="relatorioPontosAdmin-chip danger">
                      Expiração
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>Saldo Líquido</td>
                  <td>{formatarNumero(resumo.saldoLiquido)}</td>
                  <td>
                    <span className="relatorioPontosAdmin-chip primary">
                      Resultado
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default RelatorioPontos;